import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { DateTime } from "luxon";

import IconArrow from "../icons/arrow";
import Localization from "../../localization";
import { loadSpaces } from "../../redux/actions/spaces";
import { getSpaces } from "../../redux/selectors/space";
import { loadEvents } from "../../redux/actions/events";

let MAX_PER_ROW = 3;

class Calendar extends Component {
    constructor(props) {
        super(props);

        let now = DateTime.local();

        this.state = {
            visibleWeek: now.weekNumber,
            visibleYear: now.year,
            hiddenTags: [],
        };

        this.tags = [
            {
                id: "artistic",
                name: Localization[this.props.locale].calendar.artistic,
                color: "#FD88FD",
            },
            {
                id: "technical",
                name: Localization[this.props.locale].calendar.technical,
                color: "#FCDC0D",
            },
            {
                id: "reclamar_tempo",
                name: Localization[this.props.locale].calendar.reclamar_tempo,
                color: "#08ac7c",
            },
            {
                id: "reservation",
                name: Localization[this.props.locale].calendar.reservation,
                color: "#750080",
            },
            {
                id: "lesson",
                name: Localization[this.props.locale].calendar.lesson,
                color: "#4400f0",
            },
            {
                id: "public",
                name: Localization[this.props.locale].calendar.public,
                color: "#00ded1",
            },
        ];

        this._prevWeek = this._prevWeek.bind(this);
        this._nextWeek = this._nextWeek.bind(this);
        this._loadEvents = this._loadEvents.bind(this);
    }

    componentDidMount() {
        this.props.loadSpaces();
        this._loadEvents();
    }

    componentDidUpdate(prevProps, prevState) {
        if (
            this.state.visibleWeek !== prevState.visibleWeek ||
            this.state.visibleYear !== prevState.visibleYear
        ) {
            this._loadEvents();
        }
    }

    _loadEvents() {
        const { visibleWeek, visibleYear } = this.state;

        let firstDay = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        let lastDay = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 7,
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 59,
        });

        this.props.loadEvents(firstDay, lastDay);
    }

    _prevWeek() {
        const { visibleWeek, visibleYear } = this.state;

        let day = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        day = day.minus({ week: 1 });

        this.setState({
            visibleWeek: day.weekNumber,
            visibleYear: day.weekYear,
        });
    }

    _nextWeek() {
        const { visibleWeek, visibleYear } = this.state;

        let day = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        day = day.plus({ week: 1 });

        this.setState({
            visibleWeek: day.weekNumber,
            visibleYear: day.weekYear,
        });
    }

    _renderDay(day) {
        let short =
            day.weekdayShort.charAt(0).toUpperCase() +
            day.weekdayShort.slice(1);
        if (!short.includes(".")) {
            short += ".";
        }

        return (
            <div
                key={day.weekday}
                className="border-r last:border-r-0 border-blue text-center"
            >
                <p className="text-17 text-blue pb-8">
                    {short}
                    <span className="text-30 pl-6 font-judge font-bold">
                        {day.day}
                    </span>
                </p>
            </div>
        );
    }

    _renderEvent(event) {
        if (event.has_more) {
            return (
                <div className="h-full flex items-center">
                    <p className="text-12 font-judge font-bold">
                        {"+ " + event.has_more}
                    </p>
                </div>
            );
        }

        let className = "text-12 whitespace-pre-wrap";

        if (event.type === "technical") {
            className += " text-black";
        } else {
            className += " text-white";
        }

        let finalText = event.text.split("\n");

        return (
            <div className={className}>
                {finalText.map((text, index) => {
                    if (
                        (index === 1 && event.type === "lesson") ||
                        (index === 0 && event.type !== "lesson")
                    )
                        return (
                            <p key={index} className="font-bold">
                                {text}
                            </p>
                        );

                    return <p key={index}>{text}</p>;
                })}
            </div>
        );
    }

    _sortEvents(a, b) {
        let startA = DateTime.fromISO(a.start);
        let endA = DateTime.fromISO(a.end);
        let startB = DateTime.fromISO(b.start);
        let endB = DateTime.fromISO(b.end);

        let diffA = startA.diff(endA, "days").days;
        let diffB = startB.diff(endB, "days").days;

        if (diffA > diffB) {
            return 1;
        } else if (diffA < diffB) {
            return -1;
        }
        return a.key - b.key;
    }

    _renderBorder(i, maxRows, withHeight = false) {
        let className =
            "col-start-" +
            i +
            " col-end-" +
            i +
            " border-r border-blue row-start-1 row-end-" +
            (maxRows + 1);
        if (withHeight) {
            className += " h-40";
        }

        return <div key={i} className={className} />;
    }

    _renderEvents(space) {
        const { events } = this.props;
        const { visibleWeek, visibleYear } = this.state;

        let firstDay = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        let lastDay = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 7,
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 59,
        });

        let data = events
            .filter((event) => {
                if (event.space_id !== space.id) {
                    return false;
                }

                if (this.state.hiddenTags.includes(event.type)) {
                    return null;
                }

                let start = DateTime.fromISO(event.start);
                let end = DateTime.fromISO(event.end);

                if (lastDay.diff(start, "days").days < 0) {
                    return false;
                }
                if (firstDay > end) {
                    return false;
                }
                return true;
            })
            .sort(this._sortEvents);

        let maxRows = 1;
        let eventsPerDay = {};
        for (let i = 1; i <= 7; i++) {
            let filteredEvents = data.filter((event) => {
                let start = DateTime.fromISO(event.start);
                let end = DateTime.fromISO(event.end);

                return i >= start.weekday && i <= end.weekday;
            });

            maxRows = Math.max(maxRows, filteredEvents.length);

            // See used indexes
            let usedIndexes = new Array(filteredEvents.length).fill(0);
            filteredEvents.forEach((e) => {
                if (eventsPerDay[e.key]) {
                    usedIndexes[eventsPerDay[e.key].row] = 1;
                }
            });

            filteredEvents.forEach((e) => {
                if (!eventsPerDay[e.key]) {
                    let newIndex = usedIndexes.findIndex((i) => i === 0);
                    eventsPerDay[e.key] = {
                        ...e,
                        row: newIndex,
                    };
                    usedIndexes[newIndex] = 1;
                }
            });
        }

        let borders = new Array(6).fill(1);

        return (
            <div className="grid grid-cols-7 flex-1 border-t border-dashed border-blue auto-rows-fr">
                {borders.map((_, i) => this._renderBorder(i + 1, maxRows))}
                {Object.values(eventsPerDay).map((event) => {
                    let className = "h-full text-white p-2 pr-3";
                    let innerClassName =
                        "h-full p-10 rounded-2xl";

                    let start = DateTime.fromISO(event.start);
                    let end = DateTime.fromISO(event.end);

                    let startCol = start.weekday;
                    let endCol = end.weekday + 1;

                    className += " col-start-" + startCol;
                    className += " col-end-" + endCol;

                    className += " row-start-" + (event.row + 1);
                    className += " row-end-" + (event.row + 2);

                    let color = this.tags.find(
                        (tag) => tag.id === event.type
                    ).color;

                    return (
                        <div key={event.key} className={className}>
                            <div
                                className={innerClassName}
                                style={{
                                    backgroundColor: color,
                                }}
                            >
                                {this._renderEvent(event)}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }

    _renderSpaces() {
        const { spaces } = this.props;

        return (
            <div className="flex flex-col">
                {spaces.map((space) => (
                    <div key={space.id} className="flex min-h-135">
                        <div className="w-100">
                            <p className="text-blue font-judge font-bold text-17 uppercase -mt-10">
                                {space.name}
                            </p>
                        </div>
                        {this._renderEvents(space)}
                    </div>
                ))}
            </div>
        );
    }

    _renderDays() {
        const { visibleWeek, visibleYear } = this.state;

        let days = [];
        for (let i = 1; i <= 7; i++) {
            let date = DateTime.local().set({
                weekNumber: visibleWeek,
                weekYear: visibleYear,
                weekday: i,
            });
            days.push(date);
        }
        return (
            <div className="flex border-b border-blue">
                <div className="w-100" />
                <div className="grid grid-cols-7 flex-1">
                    {days.map((day) => this._renderDay(day))}
                </div>
            </div>
        );
    }

    _renderHeader() {
        const { visibleWeek, visibleYear } = this.state;

        let firstDay = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 1,
            hour: 0,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        let lastDay = DateTime.local().set({
            weekNumber: visibleWeek,
            weekYear: visibleYear,
            weekday: 7,
            hour: 23,
            minute: 59,
            second: 59,
            millisecond: 59,
        });

        let text = "";
        if (firstDay.month !== lastDay.month) {
            text =
                firstDay.day +
                " " +
                firstDay.monthShort +
                " - " +
                lastDay.day +
                " " +
                lastDay.monthShort;
        } else {
            text =
                firstDay.day + " - " + lastDay.day + " " + firstDay.monthShort;
        }

        return (
            <div className="flex flex-col xl:flex-row mb-20 xl:items-center">
                <div className="flex -ml-15 xl:mr-165">
                    <button className="p-15" onClick={this._prevWeek}>
                        <IconArrow className="w-25 h-25 transform -rotate-90" />
                    </button>
                    <div className="w-225 p-15 relative">
                        <p className="absolute text-17 text-center flex-1 top-0 left-0 right-0">
                            {firstDay.year}
                        </p>
                        <p className="font-judge text-30 font-bold uppercase text-center">
                            {text}
                        </p>
                    </div>
                    <button className="p-15" onClick={this._nextWeek}>
                        <IconArrow className="w-25 h-25 transform rotate-90" />
                    </button>
                </div>
                <div className="flex md:w-600 xl:w-800 flex-wrap justify-start">
                    {this.tags.map((tag) => (
                        <button
                            key={tag.id}
                            className="ml-0 mr-20 xl:mr-0 mt-10 xl:ml-40"
                            onClick={() => {
                                let hiddenTags = this.state.hiddenTags.slice();
                                if (hiddenTags.includes(tag.id)) {
                                    let index = this.state.hiddenTags.indexOf(
                                        tag.id
                                    );
                                    hiddenTags.splice(index, 1);
                                } else {
                                    hiddenTags.push(tag.id);
                                }
                                this.setState({
                                    hiddenTags,
                                });
                            }}
                        >
                            <div className="flex items-center">
                                <div
                                    className="w-16 xl:w-32 h-16 xl:h-32 mr-10 xl:mr-12 rounded-full"
                                    style={{
                                        backgroundColor:
                                            this.state.hiddenTags.includes(
                                                tag.id
                                            )
                                                ? "transparent"
                                                : tag.color,
                                        borderWidth: 1,
                                        borderColor: tag.color,
                                    }}
                                />
                                <p className="font-judge w-150 text-left font-bold uppercase text-14">
                                    {tag.name}
                                </p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>
        );
    }

    render() {
        let borders = new Array(6).fill(1);

        return (
            <div>
                {this._renderHeader()}
                <div className="overflow-x-auto hide-scroll">
                    <div className="border-b border-blue min-w-1024">
                        {this._renderDays()}
                        <div className="flex">
                            <div className="w-100" />
                            <div className="grid grid-cols-7 flex-1">
                                {borders.map((_, i) =>
                                    this._renderBorder(i, 1, true)
                                )}
                            </div>
                        </div>
                        {this._renderSpaces()}
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state) => ({
    locale: state.app.locale,
    spaces: getSpaces(state, true),
    events: state.events.data,
});

const mapDispatchToProps = (dispatch) => {
    return bindActionCreators(
        {
            loadEvents,
            loadSpaces,
        },
        dispatch
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(Calendar);
